import React, { useContext, useState, ChangeEvent, useEffect } from 'react';
import { Grid, Box, Divider, Checkbox, FormControlLabel, IconButton, Button, Typography, RadioGroup, Radio, OutlinedInput, InputLabel, MenuItem, FormControl, ListItemText, TextField } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { useStyles } from '../../styles';
import TextWithToggle from '../toggle';
import SimpleDropdown from '../dropdown';
import { TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { MentorshipPreferenceDetails } from '../../../../interfaces/Profile';
import { AuthContext } from '../../../../Helpers/Contexts/authContext';
import { AuthUser } from '../../../../interfaces/Contexts';
import { getPreferenceSetting, postMentorShipPreferenceSetting, postPreferenceSetting, postPreferenceSettings, postCompositePreferences } from '../../../../Utils/apiService';
import { enqueueSnackbar } from 'notistack';

dayjs.extend(utc);
dayjs.extend(timezone);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const expertise = ['Project Management'];
const format = 'HH:mm';
const language = ['Tamil', 'English'];
const menteeType = ['Undergraduates', 'Postgraduates'];

const mentorOptions = [
  'Mentor students / Alumni on career or post graduation',
  'Interview prep and resume Reviews',
  'Introduction to connections / Open doors at workplaces',
  'Offer an internship',
  'Meet for coffee / Virtual chats'
];

const menteeOptions = [
  'Mentorship on career or postgraduate applications',
  'Interview prep and resume reviews',
  'Introduction to connections / Open doors at workplaces',
  'Seek an internship',
  'Meet for coffee/ Virtual chats'
];

interface ProfilePreferencesResponse {
  data: {
    recordTypeIds: {
      Mentorship_Preferences: string;
      Offering: string;
      Seeking: string;
    };
    mentorshipPreferences: {
      Id: string;
      [key: string]: any;
    };
    offeringPrefernces: {
      Id: string;
      Mentor_on_Careers_Post_Graduation__c: boolean;
      Interview_Prep_Resume_Reviews__c: boolean;
      Make_IntrosOpen_Workplace_Doors__c: boolean;
      Offer_an_Internship__c: boolean;
      Meet_for_Coffee_Virtual_Chats__c: boolean;
      [key: string]: any;
    };
    seekingPrefernces: {
      Id: string;
      Mentor_on_CareerPG_Applications__c: boolean;
      Interview_Prep_Resume_Reviews__c: boolean;
      Make_IntrosOpen_Workplace_Doors__c: boolean;
      Seek_an_Internship__c: boolean;
      Meet_for_Coffee_Virtual_Chats__c: boolean;
      [key: string]: any;
    };
  };
}

interface CompositeRequestBody {
  RecordTypeId: string;
  Contact__c: string;
  [key: string]: any;
}

type CommunicationMethods = {
  Email: boolean;
  SMS: boolean;
  Whatsapp: boolean;
  'In-person meeting': boolean;
};

const Mentorship: React.FC<any> = (props) => {
  const classes = useStyles();
  const {
    state: { user },
  } = useContext(AuthContext);
  const userData = user as AuthUser;
  const [daySelect, setDaySelect] = React.useState<string[]>([]);
  const [expertiseSelect, setExpertiseSelect] = React.useState<string[]>([]);
  const [languageSelect, setLanguageSelect] = React.useState<string[]>([]);
  const [menteeSelect, setMenteeSelect] = React.useState<string[]>([]);
  const [mentorRoleOptions, setMentorRoleOptions] = React.useState<string[]>([]);
  const [menteeRoleOptions, setMenteeRoleOptions] = React.useState<string[]>([]);
  const [maxMentees, setMaxMentees] = React.useState<number>(0);
  const [unlimited, setUnlimited] = React.useState<boolean>(false);
  const [profilePreferences, setprofilePreferences] = useState<{
    Mentorship_Preferences: string;
    Offering: string;
    Seeking: string;
    MentorshipPreferencesId?: string;
    OfferingPreferencesId?: string;
    SeekingPreferencesId?: string;
  } | null>(null);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([dayjs('00:00:00.000Z', 'HH:mm'), dayjs('00:00:00.000Z', 'HH:mm')]);
  const [formData, setFormData] = useState<MentorshipPreferenceDetails>({
    Mentorship_Availability_Days__c: null,
    Mentorship_Availability_Start_Time__c: null,
    Mentorship_Availability_End_Time__c: null,
    Mentorship_Expertise_Areas__c: null,
    Mentorship_Languages__c: null,
    Mentee_Types__c: null,
    Mentorship_Communication_Preference__c: null,
    Show_Email_To_Mentees__c: false,
    Show_Phone_To_Mentees__c: false,
  });

  const [initialFormData, setInitialFormData] = useState<MentorshipPreferenceDetails | null>(null);
  const [isChanged, setIsChanged] = useState(false);
  const [communicationMethods, setCommunicationMethods] = useState<CommunicationMethods>({
    Email: false,
    SMS: false,
    Whatsapp: false,
    'In-person meeting': false
  });

  const [initialMentorRoleOptions, setInitialMentorRoleOptions] = useState<string[]>([]);
  const [initialMenteeRoleOptions, setInitialMenteeRoleOptions] = useState<string[]>([]);
  const [initialCommunicationMethods, setInitialCommunicationMethods] = useState<CommunicationMethods>({
    Email: false,
    SMS: false,
    Whatsapp: false,
    'In-person meeting': false
  });
  const [initialMaxMentees, setInitialMaxMentees] = useState<number>(0);
  const [initialUnlimited, setInitialUnlimited] = useState<boolean>(false);

  // Existing handlers
  const handleChangeDays = (event: SelectChangeEvent<typeof daySelect>) => {
    const {
      target: { value },
    } = event;
    setDaySelect(typeof value === 'string' ? value.split(',') : value);
  };

  const handleChangeExpertise = (event: SelectChangeEvent<typeof expertiseSelect>) => {
    const {
      target: { value },
    } = event;
    setExpertiseSelect(typeof value === 'string' ? value.split(',') : value);
  };

  const handleChangeLanguage = (event: SelectChangeEvent<typeof languageSelect>) => {
    const {
      target: { value },
    } = event;
    setLanguageSelect(typeof value === 'string' ? value.split(',') : value);
  };

  const handleChangeMentee = (event: SelectChangeEvent<typeof menteeSelect>) => {
    const {
      target: { value },
    } = event;
    setMenteeSelect(typeof value === 'string' ? value.split(',') : value);
  };

  const handleMentorRoleChange = (option: string) => {
    setMentorRoleOptions(prev =>
      prev.includes(option)
        ? prev.filter(item => item !== option)
        : [...prev, option]
    );
  };

  const handleMenteeRoleChange = (option: string) => {
    setMenteeRoleOptions(prev =>
      prev.includes(option)
        ? prev.filter(item => item !== option)
        : [...prev, option]
    );
  };

  const handleCommunicationChange = (method: keyof CommunicationMethods) => {
    setCommunicationMethods(prev => ({
      ...prev,
      [method]: !prev[method]
    }));
  };

  const handleMaxMenteesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setMaxMentees(value);
    }
  };

  const handleIncrement = () => {
    setMaxMentees(prev => prev + 1);
  };

  const handleDecrement = () => {
    setMaxMentees(prev => prev > 0 ? prev - 1 : 0);
  };

  const handleUnlimitedChange = () => {
    setUnlimited(!unlimited);
  };

  // Existing functions
  const convertTimeToISOStart = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':');
    return `${hours}:${minutes}:00.000Z`;
  };

  const convertTimeToISOEnd = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':');
    return `${hours}:${minutes}:00.000Z`;
  };

  const handleChangeTime = (times: [Dayjs | null, Dayjs | null] | null, timeStrings: [string, string] | string[]) => {
    if (times && times[0] && times[1]) {
      console.log(`Start: ${times[0].format(format)}, End: ${times[1].format(format)}`);
      const isoTimeStart = convertTimeToISOStart(`${times[0].format(format)}`);
      const isoTimeEnd = convertTimeToISOEnd(`${times[1].format(format)}`);
      setFormData({ ...formData, Mentorship_Availability_Start_Time__c: isoTimeStart, Mentorship_Availability_End_Time__c: isoTimeEnd });
      setRange(times as [Dayjs, Dayjs]);
    }
  };

  const handleChangeForm = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    console.log('Toggle Name:', name, 'Value:', checked);

    setFormData((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const formatPersonName = (names: string[]): string => {
    return names.map((name) => name.charAt(0).toUpperCase() + name.slice(1)).join(';');
  };

  const handleSave = async () => {

    const hasChanges =
      JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
      JSON.stringify(mentorRoleOptions) !== JSON.stringify(initialMentorRoleOptions) ||
      JSON.stringify(menteeRoleOptions) !== JSON.stringify(initialMenteeRoleOptions) ||
      JSON.stringify(communicationMethods) !== JSON.stringify(initialCommunicationMethods) ||
      maxMentees !== initialMaxMentees ||
      unlimited !== initialUnlimited;

    console.log('Has changes detected in handleSave:', hasChanges);

    if (!hasChanges) {
      enqueueSnackbar({
        message: 'No changes have been made to save',
        variant: 'info',
        autoHideDuration: 2000,
      });
      return;
    }

    if (!profilePreferences?.Mentorship_Preferences || !profilePreferences?.Offering || !profilePreferences?.Seeking) {
      enqueueSnackbar({ message: 'Profile preferences not loaded', variant: 'error', autoHideDuration: 1000 });
      return;
    }

    const compositeRequest = [];

    const mentorshipPrefBody = {
      Mentorship_Expertise_Areas__c: formatPersonName(expertiseSelect),
      Mentorship_Preferred_Communication_Modes__c: Object.entries(communicationMethods)
        .filter(([_, selected]) => selected)
        .map(([method]) => method)
        .join(';'),
      Max_Mentees__c: maxMentees,
      Unlimited_Mentees__c: unlimited,
      Show_Email_To_Mentees__c: formData.Show_Email_To_Mentees__c,
      Show_Phone_To_Mentees__c: formData.Show_Phone_To_Mentees__c
    };

    const offeringPrefBody = {
      Mentor_on_Careers_Post_Graduation__c: mentorRoleOptions.includes('Mentor students / Alumni on career or post graduation'),
      Interview_Prep_Resume_Reviews__c: mentorRoleOptions.includes('Interview prep and resume Reviews'),
      Make_IntrosOpen_Workplace_Doors__c: mentorRoleOptions.includes('Introduction to connections / Open doors at workplaces'),
      Offer_an_Internship__c: mentorRoleOptions.includes('Offer an internship'),
      Meet_for_Coffee_Virtual_Chats__c: mentorRoleOptions.includes('Meet for coffee / Virtual chats')
    };

    const seekingPrefBody = {
      Mentor_on_CareerPG_Applications__c: menteeRoleOptions.includes('Mentorship on career or postgraduate applications'),
      Interview_Prep_Resume_Reviews__c: menteeRoleOptions.includes('Interview prep and resume reviews'),
      Make_IntrosOpen_Workplace_Doors__c: menteeRoleOptions.includes('Introduction to connections / Open doors at workplaces'),
      Seek_an_Internship__c: menteeRoleOptions.includes('Seek an internship'),
      Meet_for_Coffee_Virtual_Chats__c: menteeRoleOptions.includes('Meet for coffee/ Virtual chats')
    };

    if (profilePreferences.MentorshipPreferencesId) {
      compositeRequest.push({
        method: "PATCH",
        url: `/services/data/v60.0/sobjects/Alm_Preferences__c/${profilePreferences.MentorshipPreferencesId}`,
        referenceId: "MentorshipPreferencesUpdate",
        body: mentorshipPrefBody
      });
    } else {
      compositeRequest.push({
        method: "POST",
        url: "/services/data/v60.0/sobjects/Alm_Preferences__c",
        referenceId: "MentorshipPreferencesUpdate",
        body: {
          ...mentorshipPrefBody,
          RecordTypeId: profilePreferences.Mentorship_Preferences,
          Contact__c: userData?.applicantId
        }
      });
    }

    if (profilePreferences.OfferingPreferencesId) {
      compositeRequest.push({
        method: "PATCH",
        url: `/services/data/v60.0/sobjects/Ken_Alumni_Preference__c/${profilePreferences.OfferingPreferencesId}`,
        referenceId: "OfferingPreferencesUpdate",
        body: offeringPrefBody
      });
    } else {
      compositeRequest.push({
        method: "POST",
        url: "/services/data/v60.0/sobjects/Ken_Alumni_Preference__c",
        referenceId: "OfferingPreferencesUpdate",
        body: {
          ...offeringPrefBody,
          RecordTypeId: profilePreferences.Offering,
          Contact__c: userData?.applicantId
        }
      });
    }

    if (profilePreferences.SeekingPreferencesId) {
      compositeRequest.push({
        method: "PATCH",
        url: `/services/data/v60.0/sobjects/Ken_Alumni_Preference__c/${profilePreferences.SeekingPreferencesId}`,
        referenceId: "SeekingPreferencesUpdate",
        body: seekingPrefBody
      });
    } else {
      compositeRequest.push({
        method: "POST",
        url: "/services/data/v60.0/sobjects/Ken_Alumni_Preference__c",
        referenceId: "SeekingPreferencesUpdate",
        body: {
          ...seekingPrefBody,
          RecordTypeId: profilePreferences.Seeking,
          Contact__c: userData?.applicantId
        }
      });
    }

    const payload = {
      allOrNone: true,
      compositeRequest
    };

    try {
      const response = await postCompositePreferences(payload as any);

      const isSuccess = response?.compositeResponse?.every((item: any) => {
        if (item.httpStatusCode === 204) {
          return true;
        }
        return item.body?.success === true;
      });

      if (isSuccess) {
        enqueueSnackbar({
          message: 'Mentorship Preferences Saved Successfully',
          variant: 'success',
          autoHideDuration: 1000,
        });
        setInitialFormData(formData);
        setIsChanged(false);
        props.setIsFormUnchanged(true);
        getMentorshipPreferenceSettings();
      } else {
        const errorMessage = response?.compositeResponse?.find((item: any) =>
          !(item.httpStatusCode === 204 || item.body?.success === true)
        )?.body?.errors?.[0]?.message || 'Something went wrong';
        enqueueSnackbar({
          message: errorMessage,
          variant: 'error',
          autoHideDuration: 1000
        });
      }
    } catch (error: any) {
      enqueueSnackbar({
        message: error?.message || 'Something went wrong',
        variant: 'error',
        autoHideDuration: 1000
      });
      console.error(error);
    }
  };

  const handleDiscard = () => {
    if (initialFormData) {
      setFormData(initialFormData);
      setDaySelect(initialFormData.Mentorship_Availability_Days__c?.split(';') || []);
      setExpertiseSelect(initialFormData.Mentorship_Expertise_Areas__c?.split(';') || []);
      setLanguageSelect(initialFormData.Mentorship_Languages__c?.split(';') || []);
      setMenteeSelect(initialFormData.Mentee_Types__c?.split(';') || []);

      // Reset communication methods
      if (initialFormData.Mentorship_Communication_Preference__c) {
        const methods = initialFormData.Mentorship_Communication_Preference__c.split(';');
        const resetMethods = {
          Email: methods.includes('Email'),
          SMS: methods.includes('SMS'),
          Whatsapp: methods.includes('Whatsapp'),
          'In-person meeting': methods.includes('In-person meeting')
        };
        setCommunicationMethods(resetMethods);
      }

      setRange([
        dayjs(initialFormData.Mentorship_Availability_Start_Time__c || '', 'HH:mm'),
        dayjs(initialFormData.Mentorship_Availability_End_Time__c || '', 'HH:mm'),
      ]);

      setIsChanged(false);
      props.setIsFormUnchanged(true);
    }
  };

  const getMentorshipPreferenceSettings = () => {
    getPreferenceSetting(userData.applicantId)
      .then((res: ProfilePreferencesResponse) => {
        console.log(res?.data?.mentorshipPreferences?.Id, "res")
        if (res?.data?.recordTypeIds) {
          const profilePreferencesId = {
            Mentorship_Preferences: res.data.recordTypeIds.Mentorship_Preferences,
            Offering: res.data.recordTypeIds.Offering,
            Seeking: res.data.recordTypeIds.Seeking,
            MentorshipPreferencesId: res.data.mentorshipPreferences?.Id,
            OfferingPreferencesId: res.data.offeringPrefernces?.Id,
            SeekingPreferencesId: res.data.seekingPrefernces?.Id
          };
          setprofilePreferences(profilePreferencesId);

          // Handle mentorship preferences
          if (res.data.mentorshipPreferences) {
            if (res.data.mentorshipPreferences.Mentorship_Availability_Start_Time__c || res.data.mentorshipPreferences.Mentorship_Availability_End_Time__c) {
              const startTime = dayjs(res.data.mentorshipPreferences.Mentorship_Availability_Start_Time__c, 'HH:mm');
              const endTime = dayjs(res.data.mentorshipPreferences.Mentorship_Availability_End_Time__c, 'HH:mm');
              setRange([startTime, endTime]);
            }

            const fetchedData = {
              Mentorship_Availability_Days__c: res.data.mentorshipPreferences.Mentorship_Availability_Days__c,
              Mentorship_Availability_Start_Time__c: res.data.mentorshipPreferences.Mentorship_Availability_Start_Time__c,
              Mentorship_Availability_End_Time__c: res.data.mentorshipPreferences.Mentorship_Availability_End_Time__c,
              Mentorship_Expertise_Areas__c: res.data.mentorshipPreferences.Mentorship_Expertise_Areas__c,
              Mentorship_Languages__c: res.data.mentorshipPreferences.Mentorship_Languages__c,
              Mentee_Types__c: res.data.mentorshipPreferences.Mentee_Types__c,
              Mentorship_Communication_Preference__c: res.data.mentorshipPreferences.Mentorship_Communication_Preference__c,
              Show_Email_To_Mentees__c: res.data.mentorshipPreferences.Show_Email_To_Mentees__c || false,
              Show_Phone_To_Mentees__c: res.data.mentorshipPreferences.Show_Phone_To_Mentees__c || false,
            };

            setFormData(fetchedData);
            setInitialFormData(fetchedData);
            setIsChanged(false);

            const mentorOptions = [];
            if (res.data.offeringPrefernces) {
              if (res.data.offeringPrefernces.Mentor_on_Careers_Post_Graduation__c) {
                mentorOptions.push('Mentor students / Alumni on career or post graduation');
              }
              if (res.data.offeringPrefernces.Interview_Prep_Resume_Reviews__c) {
                mentorOptions.push('Interview prep and resume Reviews');
              }
              if (res.data.offeringPrefernces.Make_IntrosOpen_Workplace_Doors__c) {
                mentorOptions.push('Introduction to connections / Open doors at workplaces');
              }
              if (res.data.offeringPrefernces.Offer_an_Internship__c) {
                mentorOptions.push('Offer an internship');
              }
              if (res.data.offeringPrefernces.Meet_for_Coffee_Virtual_Chats__c) {
                mentorOptions.push('Meet for coffee / Virtual chats');
              }
            }
            setMentorRoleOptions(mentorOptions);
            setInitialMentorRoleOptions(mentorOptions);

            const menteeOptions = [];
            if (res.data.seekingPrefernces) {
              if (res.data.seekingPrefernces.Mentor_on_CareerPG_Applications__c) {
                menteeOptions.push('Mentorship on career or postgraduate applications');
              }
              if (res.data.seekingPrefernces.Interview_Prep_Resume_Reviews__c) {
                menteeOptions.push('Interview prep and resume reviews');
              }
              if (res.data.seekingPrefernces.Make_IntrosOpen_Workplace_Doors__c) {
                menteeOptions.push('Introduction to connections / Open doors at workplaces');
              }
              if (res.data.seekingPrefernces.Seek_an_Internship__c) {
                menteeOptions.push('Seek an internship');
              }
              if (res.data.seekingPrefernces.Meet_for_Coffee_Virtual_Chats__c) {
                menteeOptions.push('Meet for coffee/ Virtual chats');
              }
            }
            setMenteeRoleOptions(menteeOptions);
            setInitialMenteeRoleOptions(menteeOptions);

            const methods = res.data.mentorshipPreferences.Mentorship_Communication_Preference__c?.split(';') || [];
            const commMethods: CommunicationMethods = {
              Email: methods.includes('Email'),
              SMS: methods.includes('SMS'),
              Whatsapp: methods.includes('Whatsapp'),
              'In-person meeting': methods.includes('In-person meeting')
            };

            // Update both current and initial states with the same typed object
            const typedCommMethods: CommunicationMethods = { ...commMethods };
            setCommunicationMethods(typedCommMethods);
            setInitialCommunicationMethods(typedCommMethods);

            const isUnlimited = res.data.mentorshipPreferences.Unlimited_Mentees__c || false;
            setUnlimited(isUnlimited);
            setInitialUnlimited(isUnlimited);

            const maxMenteesValue = res.data.mentorshipPreferences.Max_Mentees__c || 0;
            setMaxMentees(maxMenteesValue);
            setInitialMaxMentees(maxMenteesValue);

            if (res.data.mentorshipPreferences.Mentorship_Availability_Days__c) {
              const days = res.data.mentorshipPreferences.Mentorship_Availability_Days__c.split(';');
              setDaySelect(days);
            }
            if (res.data.mentorshipPreferences.Mentorship_Expertise_Areas__c) {
              const expertise = res.data.mentorshipPreferences.Mentorship_Expertise_Areas__c.split(';');
              setExpertiseSelect(expertise);
            }
            if (res.data.mentorshipPreferences.Mentorship_Languages__c) {
              const languages = res.data.mentorshipPreferences.Mentorship_Languages__c.split(';');
              setLanguageSelect(languages);
            }
            if (res.data.mentorshipPreferences.Mentee_Types__c) {
              const mentee = res.data.mentorshipPreferences.Mentee_Types__c.split(';');
              setMenteeSelect(mentee);
            }
          }

          // Handle offering preferences (I'm willing to be a mentor)
          if (res.data.offeringPrefernces) {
            const mentorOptions = [];
            if (res.data.offeringPrefernces.Mentor_on_Careers_Post_Graduation__c) {
              mentorOptions.push('Mentor students / Alumni on career or post graduation');
            }
            if (res.data.offeringPrefernces.Interview_Prep_Resume_Reviews__c) {
              mentorOptions.push('Interview prep and resume Reviews');
            }
            if (res.data.offeringPrefernces.Make_IntrosOpen_Workplace_Doors__c) {
              mentorOptions.push('Introduction to connections / Open doors at workplaces');
            }
            if (res.data.offeringPrefernces.Offer_an_Internship__c) {
              mentorOptions.push('Offer an internship');
            }
            if (res.data.offeringPrefernces.Meet_for_Coffee_Virtual_Chats__c) {
              mentorOptions.push('Meet for coffee / Virtual chats');
            }
            setMentorRoleOptions(mentorOptions);
          }

          // Handle seeking preferences (I need a mentor)
          if (res.data.seekingPrefernces) {
            const menteeOptions = [];
            if (res.data.seekingPrefernces.Mentor_on_CareerPG_Applications__c) {
              menteeOptions.push('Mentorship on career or postgraduate applications');
            }
            if (res.data.seekingPrefernces.Interview_Prep_Resume_Reviews__c) {
              menteeOptions.push('Interview prep and resume reviews');
            }
            if (res.data.seekingPrefernces.Make_IntrosOpen_Workplace_Doors__c) {
              menteeOptions.push('Introduction to connections / Open doors at workplaces');
            }
            if (res.data.seekingPrefernces.Seek_an_Internship__c) {
              menteeOptions.push('Seek an internship');
            }
            if (res.data.seekingPrefernces.Meet_for_Coffee_Virtual_Chats__c) {
              menteeOptions.push('Meet for coffee/ Virtual chats');
            }
            setMenteeRoleOptions(menteeOptions);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    getMentorshipPreferenceSettings();
  }, []);

  useEffect(() => {
    if (initialFormData) {
      console.log('Change detection running...');

      const formDataChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      const mentorOptionsChanged = JSON.stringify(mentorRoleOptions) !== JSON.stringify(initialMentorRoleOptions);
      const menteeOptionsChanged = JSON.stringify(menteeRoleOptions) !== JSON.stringify(initialMenteeRoleOptions);
      const communicationChanged = JSON.stringify(communicationMethods) !== JSON.stringify(initialCommunicationMethods);
      const maxMenteesChanged = maxMentees !== initialMaxMentees;
      const unlimitedChanged = unlimited !== initialUnlimited;

      console.log('Change detection results:', {
        formDataChanged,
        mentorOptionsChanged,
        menteeOptionsChanged,
        communicationChanged,
        maxMenteesChanged,
        unlimitedChanged
      });

      const hasChanged =
        formDataChanged ||
        mentorOptionsChanged ||
        menteeOptionsChanged ||
        communicationChanged ||
        maxMenteesChanged ||
        unlimitedChanged;

      console.log('Final hasChanged value:', hasChanged);

      setIsChanged(hasChanged);
      props.setIsFormUnchanged(!hasChanged);
    }
  }, [
    formData,
    initialFormData,
    mentorRoleOptions,
    menteeRoleOptions,
    communicationMethods,
    maxMentees,
    unlimited,
    initialMentorRoleOptions,
    initialMenteeRoleOptions,
    initialCommunicationMethods,
    initialMaxMentees,
    initialUnlimited
  ]);

  return (
    <Box className={classes.entireContainer} sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
      <Typography
        sx={{
          fontSize: '15px',
          pb: 2,
          fontWeight: '600',
          '@media (min-width: 1280px) and (max-width: 1300px)': {
            fontSize: '13px !important',
          },
        }}
        textAlign="start"
      >
        Preferences
      </Typography>
      <Divider className={classes.divider} />


      <Box
        // sx={{ padding: '30px 0px' }}
        className={`${classes.sepContainer1} scrollbar-cusgray mt-4`}
      >
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 1 }}>
          <Typography sx={{
            mb: 3,
            fontWeight: 500,
            color: '#373A45;',
            fontSize: '18px',
            textAlign: 'left'
          }} textAlign="start">
            I'm Willing to Be a Mentor
          </Typography>
          <Grid container spacing={2}>
            {mentorOptions.map((option, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mentorRoleOptions.includes(option)}
                      onChange={() => handleMentorRoleChange(option)}
                      sx={{
                        color: 'black',
                        '&.Mui-checked': { color: '#F7A70' },
                        padding: '4px 8px 4px 0',
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '14px', textAlign: "start", color: "#585A64" }}>{option}</Typography>}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      marginLeft: '4px'
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* I need a mentor section */}
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 1 }}>
          <Typography sx={{
            mb: 3,
            fontWeight: 500,
            color: '#373a45',
            fontSize: '18px',
            textAlign: 'left'
          }} textAlign="start">
            I Need a Mentor
          </Typography>
          <Grid container spacing={2}>
            {menteeOptions.map((option, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={menteeRoleOptions.includes(option)}
                      onChange={() => handleMenteeRoleChange(option)}
                      sx={{
                        color: 'black',
                        '&.Mui-checked': { color: '#F7A70' },
                        padding: '4px 8px 4px 0',
                      }}
                    />
                  }
                  label={<Typography sx={{ fontSize: '14px', textAlign: "start", color: "#585A64" }}>{option}</Typography>}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      marginLeft: '4px'
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>


        {/* Areas of expertise */}
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 1, textAlign: "start" }}>
          <Typography sx={{
            fontSize: '18px', fontWeight: 500,
            color: '#373a45', mb: 2
          }} textAlign="start">
            Areas of Expertise
          </Typography>
          <Typography sx={{ fontSize: '14px', mb: 1 }} textAlign="start">
            Select expertise
          </Typography>
          <FormControl sx={{ width: '100%', maxWidth: '400px' }}>
            <Select
              displayEmpty
              multiple
              value={expertiseSelect}
              onChange={handleChangeExpertise}
              renderValue={(selected) =>
                selected.length === 0 ? 'Select expertise' : selected.join(', ')
              }
              MenuProps={MenuProps}
              size="small"
              sx={{
                backgroundColor: 'white',
                height: '40px',
                '.MuiSelect-select': {
                  fontSize: '14px',
                  textAlign: 'left',
                  paddingLeft: '10px',
                },
              }}
            >
              {expertise.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={expertiseSelect.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Mentorship preferences */}
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 1 }}>
          <Typography sx={{
            fontSize: '18px', fontWeight: 500,
            color: '#373a45', mb: 2
          }} textAlign="start">
            Mentorship Preferences
          </Typography>
          <Typography sx={{ fontSize: '14px', mb: 1 }} textAlign="start">
            Preferred method of communication
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {Object.entries(communicationMethods).map(([method, selected]) => (
              <FormControlLabel
                key={method}
                control={
                  <Checkbox
                    checked={selected}
                    onChange={() => handleCommunicationChange(method as keyof CommunicationMethods)}
                    sx={{
                      color: 'black',
                      '&.Mui-checked': { color: '#F7A70' },
                      padding: '4px 8px 4px 0',
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '14px' }}>{method}</Typography>}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                  marginRight: '16px',
                  '& .MuiFormControlLabel-label': {
                    marginLeft: '4px'
                  }
                }}
              />
            ))}
          </Box>

          <Typography sx={{ fontSize: '14px', mb: 1 }} textAlign="start">
            Maximum number of people I want to mentor at a time
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Number input with horizontal +/- buttons */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #d0d0d0',
              borderRadius: '4px',
              height: '40px',
              bgcolor: 'white',
              overflow: 'hidden',
              width: 'fit-content'
            }}>
              <input
                type="number"
                value={maxMentees}
                onChange={handleMaxMenteesChange}
                style={{
                  border: 'none',
                  outline: 'none',
                  textAlign: 'left',
                  fontSize: '14px',
                  width: '80px',
                  height: '38px',
                  padding: '0 12px',
                  backgroundColor: 'white'
                }}
                min="0"
              />
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                height: '38px'
              }}>
                <button
                  onClick={handleDecrement}
                  disabled={maxMentees === 0}
                  style={{
                    width: '30px',
                    height: '38px',
                    borderRight: 'none',
                    border: "none",
                    backgroundColor: 'white',
                    cursor: maxMentees === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: maxMentees === 0 ? '#ccc' : '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                // onMouseOver={(e) => {
                //   if (maxMentees > 0) e.target.style.backgroundColor = '#f5f5f5'
                // }}
                // onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                >
                  −
                </button>
                <button
                  onClick={handleIncrement}
                  style={{
                    width: '30px',
                    height: '38px',
                    borderLeft: 'none',
                    border: "none",
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                // onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                // onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                >
                  +
                </button>
              </Box>

            </Box>

            {/* Unlimited option */}
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleUnlimitedChange}>
              <Box sx={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                border: unlimited ? '5px solid #f4a261' : '2px solid #d0d0d0',
                bgcolor: unlimited ? '#f4a261' : 'transparent',
                mr: 1,
                flexShrink: 0
              }} />
              <Typography sx={{ fontSize: '14px' }}>
                Unlimited
              </Typography>
            </Box>
          </Box>
        </Box>


        {/* Privacy and confidentiality */}
        <Box sx={{ mb: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 1 }}>
          <Typography sx={{
            fontSize: '18px', fontWeight: 500,
            color: '#373a45', mb: 2
          }} textAlign="start">
            Privacy and Confidentiality
          </Typography>

          <Typography sx={{ fontSize: '14px', mb: 2 }} textAlign="start">
            Manage who can see your contact information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextWithToggle
                toggleName={'Show Email'}
                isChecked={formData.Show_Email_To_Mentees__c}
                handleToggleChange={handleToggleChange}
                name="Show_Email_To_Mentees__c"
                labelSx={{ pl: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextWithToggle
                toggleName={'Show Phone'}
                isChecked={formData.Show_Phone_To_Mentees__c}
                handleToggleChange={handleToggleChange}
                name="Show_Phone_To_Mentees__c"
                labelSx={{ pl: 2 }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box sx={{
        mt: 'auto',
        display: 'flex',
        justifyContent: "end",
        alignItems: 'center',
        p: 2,
        background: { xs: '#fff', md: 'transparent' },
        gap: 1,
        "@media (max-width: 600px)": {
          justifyContent: 'flex-end',
        },
      }}>
        <Button
          variant='contained'
          color='secondary'
          // fullWidth

          disabled={!isChanged} // Disable if no changes
          onClick={handleDiscard}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: '500', color: '#373A45' }}>Discard Changes</Typography>
        </Button>
        <Button
          variant='contained'
          // fullWidth

          color='primary'
          onClick={handleSave}
        // disabled={!isChanged} // Disable if no changes

        >
          <Typography sx={{ fontSize: '12px', fontWeight: '500', color: '#5B4200' }}>Save Changes</Typography>
        </Button>
      </Box>
    </Box>
  );
};
export default Mentorship;
export { default as MentorshipPreferenceModal } from './MentorshipPreferenceModal';
